import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Loader2, ArrowRight, CreditCard, ChevronDown, Sparkles, Check,
  Minimize2, Layers, Image as ImageIcon, ScanText, Scissors, RotateCw, ImageDown, Stamp, EyeOff, FileText, Lock, LockOpen, Eraser, FilePen, Receipt,
} from "lucide-react";
import { Logo, LoadingMark } from "@/components/Logo";
import { PdfTool } from "@/components/PdfTool";
import { useAuth } from "@/context/AuthContext";
import { CARD_SHADOW, spring, useUITheme, ThemeToggle } from "@/components/ios";
import { loadPending, clearPending } from "@/utils/pendingFile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_ICONS = {
  compress: Minimize2, merge: Layers, img2pdf: ImageIcon, extract: ScanText, pages: Scissors,
  rotate: RotateCw, pdf2img: ImageDown, pdf2docx: FileText, watermark: Stamp, redact: EyeOff,
  protect: Lock, unlock: LockOpen, scrub: Eraser, edit: FilePen, invoice: Receipt,
};

const FALLBACK_ENGINES = [
  { id: "compress", name: "Compress", tagline: "Shrink PDFs up to 85%", description: "", best_for: "", limits: "" },
  { id: "merge", name: "Merge", tagline: "Many PDFs into one", description: "", best_for: "", limits: "" },
  { id: "img2pdf", name: "Images to PDF", tagline: "JPG / PNG / WEBP in", description: "", best_for: "", limits: "" },
  { id: "extract", name: "Extract text", tagline: "Every word, out", description: "", best_for: "", limits: "" },
  { id: "pages", name: "Split & reorder", tagline: "Keep only what you need", description: "", best_for: "", limits: "" },
  { id: "rotate", name: "Rotate", tagline: "90 / 180 / 270 degrees", description: "", best_for: "", limits: "" },
  { id: "pdf2img", name: "PDF to PNG", tagline: "Every page as an image", description: "", best_for: "", limits: "" },
  { id: "pdf2docx", name: "PDF to Word", tagline: "Editable DOCX from any PDF", description: "", best_for: "", limits: "" },
  { id: "watermark", name: "Watermark", tagline: "Stamp CONFIDENTIAL & more", description: "", best_for: "", limits: "" },
  { id: "redact", name: "Redact", tagline: "Permanent text & box blackouts", description: "", best_for: "", limits: "" },
  { id: "protect", name: "Protect", tagline: "AES-256 password lock", description: "", best_for: "", limits: "" },
  { id: "unlock", name: "Unlock", tagline: "Remove a known password", description: "", best_for: "", limits: "" },
  { id: "scrub", name: "Scrub metadata", tagline: "Strip hidden document data", description: "", best_for: "", limits: "" },
  { id: "edit", name: "Edit PDF", tagline: "Add text, rotate, drop pages", description: "", best_for: "", limits: "" },
  { id: "invoice", name: "Invoice builder", tagline: "Client-ready invoices in seconds", description: "", best_for: "", limits: "" },
];

const PRO_BENEFITS = [
  "Unlimited conversions — no monthly cap",
  "50MB per file for batch-size diligence packets",
  "Batch processing for closing-size document sets",
  "14-day free trial — cancel anytime",
];

const fmtBytes = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)}MB` : `${Math.max(1, Math.round(b / 1024))}KB`);

const timeAgo = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const themeClasses = (dk) => ({
  page: dk ? "v4-theme bg-[#0C0A09] text-white" : "bg-[#FDFDFB] text-[#111110]",
  header: dk ? "bg-black/60 border-white/[0.08]" : "bg-white border-black/[0.08]",
  card: dk ? "bg-[#1C1C1E] border border-white/[0.08]" : `bg-white border border-hairline ${CARD_SHADOW}`,
  primary: dk ? "text-white" : "text-[#111110]",
  secondary: dk ? "text-white/55" : "text-[#3C3C43]/60",
  tertiary: dk ? "text-white/35" : "text-[#3C3C43]/40",
  divider: dk ? "divide-white/[0.07]" : "divide-black/[0.05]",
  rowActive: dk ? "active:bg-white/[0.06]" : "active:bg-black/[0.03]",
  iconTile: dk ? "bg-[#D4A373]/15" : "bg-[#002B5B]/[0.08]",
  iconColor: dk ? "text-[#D4A373]" : "text-[#002B5B]",
  link: dk ? "text-[#D4A373]" : "text-[#002B5B]",
  statusPill: dk ? "text-[#D4A373] bg-[#D4A373]/15" : "text-[#002B5B] bg-[#002B5B]/[0.08]",
  tag: dk ? "bg-white/[0.08] text-white/60" : "bg-black/[0.05] text-[#3C3C43]/70",
  signedBanner: dk ? "bg-[#30D158]/[0.12] border-[#30D158]/25" : "bg-[#34C759]/[0.08] border-[#34C759]/20",
  signedText: dk ? "text-[#30D158]" : "text-[#248A3D]",
  successDot: dk ? "bg-[#30D158]" : "bg-[#34C759]",
  upsell: dk ? "bg-[#D4A373] text-[#0C0A09]" : "bg-[#111110] text-white",
  upsellBtn: dk ? "bg-[#FFFFFF] text-[#111110]" : "bg-white text-[#111110]",
  banner: dk ? "bg-[#FF9F0A]/[0.12] border-[#FF9F0A]/30" : "bg-[#FF9500]/10 border-[#FF9500]/25",
});

const EngineRow = ({ engine, index, c }) => {
  const [open, setOpen] = useState(false);
  const Icon = ENGINE_ICONS[engine.id] || FileText;
  return (
    <div data-testid={`engine-row-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`engine-toggle-${engine.id}`}
        className={`w-full flex items-center gap-3.5 px-5 py-3.5 text-left min-h-[56px] transition-colors duration-150 ${c.rowActive}`}
      >
        <span className={`w-8 h-8 rounded-lg ${c.iconTile} flex items-center justify-center shrink-0`}>
          <Icon className={`w-4 h-4 ${c.iconColor}`} strokeWidth={1.5} />
        </span>
        <span className="flex-1 min-w-0">
          <span className={`block text-[15px] font-semibold ${c.primary}`}>{engine.name}</span>
          <span className={`block text-[13px] ${c.secondary} truncate`}>{engine.tagline}</span>
        </span>
        <ChevronDown className={`w-4 h-4 ${c.tertiary} transition-transform duration-200 ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={spring}
          className="px-5 pb-4 pl-[62px]"
          data-testid={`engine-detail-${engine.id}`}
        >
          {engine.description && <p className={`text-[14px] leading-relaxed ${c.secondary} mb-2`}>{engine.description}</p>}
          {engine.best_for && (
            <p className={`text-[13px] ${c.secondary} mb-2`}>
              <span className={`font-semibold ${c.primary}`}>Best for:</span> {engine.best_for}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {engine.output && (
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${c.tag}`}>{engine.output}</span>
            )}
            {engine.limits && (
              <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${c.statusPill}`}>{engine.limits}</span>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default function Workspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(undefined);
  const [usage, setUsage] = useState(null);
  const [history, setHistory] = useState([]);
  const [engines, setEngines] = useState(FALLBACK_ENGINES);
  const [pending, setPending] = useState(undefined);
  const [resumeBanner, setResumeBanner] = useState(false);
  const { theme, dark: dk, toggle: toggleTheme } = useUITheme();
  const c = themeClasses(dk);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const refresh = useCallback(() => {
    if (!user) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem("doclyx_token")}` };
    axios
      .get(`${API}/account/subscription`, { headers, withCredentials: true })
      .then((res) => {
        setPlan(res.data.plan);
        setUsage(res.data.usage);
      })
      .catch(() => setPlan(null));
    axios
      .get(`${API}/workspace/history`, { headers, withCredentials: true })
      .then((res) => setHistory(res.data.runs))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    refresh();
    axios.get(`${API}/engines`).then((res) => setEngines(res.data.engines)).catch(() => {});
  }, [refresh]);

  const resumeChecked = useRef(false);
  useEffect(() => {
    if (!plan || resumeChecked.current) return;
    resumeChecked.current = true;
    loadPending().then((p) => {
      if (p?.files?.length) {
        setPending(p);
        setResumeBanner(true);
        clearPending();
      } else {
        setPending(null);
      }
    });
  }, [plan]);

  if (!user || plan === undefined) {
    return (
      <main className={`${dk ? "v4-theme bg-[#0C0A09]" : "bg-[#FDFDFB]"} min-h-screen flex items-center justify-center`}>
        <LoadingMark label="Loading workspace" />
      </main>
    );
  }

  const isIndependent = plan?.lookup_key === "independent_monthly";
  const remaining = usage?.limit ? Math.max(0, usage.limit - usage.used) : null;

  return (
    <div className={`ws-root min-h-screen font-sans ${c.page}`} data-testid="workspace-page" data-theme={theme}>
      <header className={`sticky top-0 z-40 ${c.header} backdrop-blur-2xl backdrop-saturate-150 border-b`}>
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/account" data-testid="workspace-home-link"><Logo size={26} /></Link>
          <div className="flex items-center gap-3">
            <ThemeToggle dark={dk} onToggle={toggleTheme} testid="workspace-theme-toggle" />
            <Link
              to="/account"
              data-testid="workspace-account-link"
              className={`text-[13px] font-medium ${c.link} min-h-[44px] flex items-center active:scale-95 transition-transform duration-150`}
            >
              Account
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8 md:py-12">
        {!plan ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={spring}
            className={`max-w-xl mx-auto rounded-[32px] p-8 md:p-12 ${c.card}`}
            data-testid="workspace-locked"
          >
            <span className={`inline-flex items-center gap-1.5 text-[12px] font-semibold ${c.statusPill} rounded-full px-3 py-1.5 mb-5`}>
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> 14-day free trial
            </span>
            <h1 className={`text-2xl md:text-3xl font-bold tracking-tight mb-3 ${c.primary}`}>Your workspace is waiting.</h1>
            <p className={`text-[15px] leading-relaxed ${c.secondary} mb-6`}>
              One plan unlocks all fifteen engines — compress, merge, redact, password-protect, Word export and more.
              Professional starts free for 14 days.
            </p>
            <div className={`rounded-sm p-5 mb-7 ${dk ? "bg-white/[0.05]" : "bg-[#FDFDFB]"}`}>
              {PRO_BENEFITS.map((b) => (
                <p key={b} className={`flex items-start gap-2.5 text-[14px] ${c.primary} py-1.5`}>
                  <Check className="w-4 h-4 text-[#34C759] shrink-0 mt-0.5" strokeWidth={2.5} /> {b}
                </p>
              ))}
            </div>
            <Link
              to="/#pricing"
              data-testid="workspace-view-plans-link"
              className={`inline-flex items-center justify-center gap-2 w-full min-h-[48px] rounded-full ${dk ? "bg-[#0A84FF]" : "bg-[#007AFF]"} text-white text-[15px] font-semibold active:scale-95 transition-transform duration-150`}
            >
              View plans <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={spring}>
            <h1 className={`text-2xl md:text-3xl font-bold tracking-tight mb-8 ${c.primary}`} data-testid="workspace-title">
              Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening"},{" "}
              {user.name?.split(" ")[0] || "there"}.
            </h1>

            {remaining !== null && remaining <= 3 && (
              <div className={`mb-6 rounded-sm border p-4 flex items-center justify-between gap-4 ${c.banner}`} data-testid="workspace-cap-banner">
                <p className={`text-[14px] font-medium ${c.primary}`}>
                  {remaining === 0 ? "You've used all 20 conversions this month." : `Only ${remaining} conversion${remaining === 1 ? "" : "s"} left this month.`}
                </p>
                <Link to="/billing" data-testid="workspace-cap-upgrade" className={`shrink-0 text-[13px] font-semibold ${c.link} min-h-[44px] flex items-center`}>
                  Go unlimited
                </Link>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6 items-start">
              <aside className="space-y-6 lg:sticky lg:top-24">
                <div className={`rounded-3xl p-6 ${c.card}`} data-testid="workspace-plan-card">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`text-[13px] font-medium ${c.secondary}`}>Your plan</span>
                    <span className={`text-[11px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-1 ${c.statusPill}`} data-testid="workspace-plan-status">
                      {plan.status}
                    </span>
                  </div>
                  <p className={`text-xl font-bold tracking-tight mb-4 ${c.primary}`} data-testid="workspace-plan-badge">{plan.name}</p>
                  {usage && (
                    <>
                      <div className={`flex justify-between text-[13px] ${c.secondary} mb-2`}>
                        <span>Conversions this month</span>
                        <span className={`font-semibold tabular-nums ${c.primary}`} data-testid="workspace-usage-pill">
                          {usage.limit ? `${usage.used} / ${usage.limit}` : `${usage.used} · unlimited`}
                        </span>
                      </div>
                      {usage.limit && (
                        <div className={`w-full h-1.5 ${dk ? "bg-white/10" : "bg-black/[0.06]"} rounded-full overflow-hidden`}>
                          <div
                            className={`h-full rounded-full transition-[width] duration-500 ${remaining !== null && remaining <= 3 ? "bg-[#FF9500]" : dk ? "bg-[#0A84FF]" : "bg-[#007AFF]"}`}
                            style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }}
                            data-testid="workspace-usage-bar"
                          />
                        </div>
                      )}
                    </>
                  )}
                  <Link
                    to="/billing"
                    data-testid="workspace-manage-link"
                    className={`flex items-center gap-2 text-[14px] font-medium ${c.link} mt-5 min-h-[44px] active:scale-95 transition-transform duration-150`}
                  >
                    <CreditCard className="w-4 h-4" strokeWidth={1.5} /> Manage subscription
                  </Link>
                </div>

                {isIndependent && (
                  <div className={`rounded-3xl p-6 ${c.upsell}`} data-testid="workspace-upgrade-card">
                    <p className="text-[17px] font-semibold tracking-tight mb-1.5">Go unlimited with Professional.</p>
                    <p className={`text-[13px] mb-4 ${dk ? "text-[#0C0A09]/70" : "text-white/65"}`}>Remove the 20-run cap and 15MB file limit. Starts with 14 days free.</p>
                    <div className="space-y-2 mb-5">
                      {PRO_BENEFITS.slice(0, 3).map((b) => (
                        <p key={b} className={`flex items-start gap-2 text-[13px] ${dk ? "text-[#0C0A09]/85" : "text-white/85"}`}>
                          <Check className="w-3.5 h-3.5 text-[#34C759] shrink-0 mt-0.5" strokeWidth={2.5} /> {b}
                        </p>
                      ))}
                    </div>
                    <Link
                      to="/billing"
                      data-testid="workspace-upgrade-nudge"
                      className={`flex items-center justify-center gap-2 w-full min-h-[44px] rounded-full ${c.upsellBtn} text-[14px] font-semibold active:scale-95 transition-transform duration-150`}
                    >
                      Upgrade to Professional <ArrowRight className="w-4 h-4" strokeWidth={2} />
                    </Link>
                  </div>
                )}

                <div className={`rounded-3xl overflow-hidden ${c.card}`} data-testid="engine-directory">
                  <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                    <span className={`text-[13px] font-medium ${c.secondary}`}>Engine directory</span>
                    <span className={`text-[11px] font-semibold ${c.tertiary}`}>{engines.length} engines</span>
                  </div>
                  <div className={`divide-y ${c.divider}`}>
                    {engines.map((e, i) => (
                      <EngineRow key={e.id} engine={e} index={i} c={c} />
                    ))}
                  </div>
                </div>
              </aside>

              <section className="space-y-6">
                <div id="workspace-tool" data-testid="workspace-tool" className="scroll-mt-24">
                  {resumeBanner && pending && (
                    <div className={`mb-4 rounded-sm border p-4 flex items-center justify-between gap-4 ${dk ? "bg-[#0A84FF]/[0.12] border-[#0A84FF]/30" : "bg-[#007AFF]/[0.07] border-[#007AFF]/20"}`} data-testid="resume-banner">
                      <p className={`text-[14px] font-medium ${c.primary}`}>
                        Your file came with you — {pending.files.length} file{pending.files.length === 1 ? "" : "s"} loaded, ready mid-task.
                      </p>
                      <button
                        type="button"
                        onClick={() => setResumeBanner(false)}
                        data-testid="resume-banner-dismiss"
                        className={`shrink-0 text-[13px] font-semibold ${c.link}`}
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  <div className={`rounded-[32px] overflow-hidden ${dk ? "v4-theme" : ""}`} data-testid="workspace-console">
                    {pending !== undefined && (
                      <PdfTool
                        key={pending ? "resume" : "fresh"}
                        token={localStorage.getItem("doclyx_token")}
                        onComplete={refresh}
                        maxMb={isIndependent ? 15 : 50}
                        light={!dk}
                        initialFiles={pending?.files || null}
                        initialAction={pending?.action || null}
                      />
                    )}
                  </div>
                </div>

                <div id="workspace-history" className={`rounded-3xl overflow-hidden scroll-mt-24 ${c.card}`} data-testid="workspace-history">
                  <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                    <span className={`text-[13px] font-medium ${c.secondary}`}>Recent runs</span>
                    <span className={`text-[11px] font-semibold ${c.tertiary}`}>{history.length} shown</span>
                  </div>
                  <div className={`divide-y ${c.divider}`}>
                    {history.length === 0 && (
                      <p className={`px-5 py-6 text-[14px] ${c.secondary}`} data-testid="history-empty">
                        Nothing yet — your first conversion will appear here.
                      </p>
                    )}
                    {history.map((r, i) => (
                      <div key={`${r.at}-${r.action}-${r.bytes}`} className="flex items-center gap-3.5 px-5 py-3.5" data-testid={`history-row-${i}`}>
                        <span className={`w-2 h-2 rounded-full shrink-0 ${r.ok ? c.successDot : "bg-[#FF3B30]"}`} />
                        <span className={`text-[14px] font-semibold ${c.primary} w-24 shrink-0 capitalize`}>{r.action}</span>
                        <span className={`text-[13px] ${c.secondary} flex-1`}>
                          {r.file_count} file{r.file_count === 1 ? "" : "s"} · {fmtBytes(r.bytes)}
                        </span>
                        <span className={`text-[12px] ${c.tertiary} shrink-0`}>{timeAgo(r.at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
